/**
 * UserSearchCombobox — the ONE "search a person, pick one" control.
 *
 * Collapses three near-identical dropdowns (admin UserSelect, the handover
 * ShiftNursePicker search fallback, ShareDialog's people picker) into a single
 * implementation. The two things those three disagreed on are the two things
 * this component parameterises:
 *
 *   - where the candidates come from (`dataSource`): a client-side list already
 *     in memory (the admin directory query) or a server-side typeahead;
 *   - what the caller does with the pick (`onSelect`).
 *
 * Everything else — portalling, anchoring, keyboard operation, dismissal — is
 * the same problem in all three places and is solved once, here.
 *
 * The suggestion list is rendered in a PORTAL, `position: fixed`, anchored to
 * the input's bounding rect — NOT as an `absolute` child. Every caller lives
 * inside a Radix Dialog/Sheet, and DialogContent is a `transform`-positioned,
 * `overflow-y-auto` box: an in-flow `absolute` dropdown is both clipped by that
 * overflow and trapped in the dialog's transform containing block (so even
 * `position: fixed` would clip). Portalling to <body> escapes both, so the list
 * can never be cut off by the dialog — on desktop or mobile.
 */

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  MIN_USER_SEARCH_QUERY_LENGTH,
  SEARCH_DEBOUNCE_MS,
} from '@/lib/constants';
import type { UserSearchResult } from '@/api/endpoints/users';

/** Where the candidate users come from. `client` = an already-fetched list
 *  filtered in memory (its `error` says the fetch failed, which is NOT the same
 *  as an empty directory); `server` = a typeahead, debounced and sequence-
 *  guarded here. */
export type SearchDataSource =
  | { type: 'client'; users: UserSearchResult[] | undefined; error?: boolean }
  | {
      type: 'server';
      searchFn: (
        query: string,
      ) => Promise<{ results: UserSearchResult[]; totalCount: number | null }>;
    };

export interface UserSearchComboboxProps {
  /** Input text — controlled by the caller, so a pick can rewrite it. */
  value: string;
  onChange: (value: string) => void;
  onSelect: (user: UserSearchResult) => void;
  dataSource: SearchDataSource;
  /** Users to hide from the list (self, the outgoing nurse, already-listed colleagues). */
  excludeIds?: string[];
  /** The user currently chosen — marked with a check in the list. */
  selectedUserId?: string | null;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
}

interface AnchorRect {
  top: number;
  left: number;
  width: number;
}

/** 'idle' = nothing asked for yet (query too short); 'ready' = a search
 *  completed, so zero results genuinely means "nobody matches". The
 *  distinction is what keeps "No users found" from appearing before we have
 *  actually looked. */
type ServerStatus = 'idle' | 'searching' | 'ready' | 'failed';


/** The best true thing we know about this user. `id` always exists, so there
 *  is never a reason to invent an "Unknown". */
export function userDisplayName(user: UserSearchResult): string {
  if (user.name) return user.name;
  if (user.email) return user.email;
  return user.id;
}

/** Secondary line: only the fields this user actually has, minus whatever the
 *  primary line already said. Renders as nothing when there is nothing to say. */
function userDetail(user: UserSearchResult): string {
  const primary = userDisplayName(user);
  return [user.email, user.role, user.specialisation, user.unit]
    .filter((v): v is string => !!v && v !== primary)
    .join(' · ');
}

export function UserSearchCombobox({
  value,
  onChange,
  onSelect,
  dataSource,
  excludeIds,
  selectedUserId,
  id,
  placeholder,
  disabled,
}: UserSearchComboboxProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  // Keyboard-highlighted option (-1 = none). Drives aria-activedescendant and
  // Enter-to-select, so the combobox ARIA contract is actually operable for
  // keyboard / switch-access users, not just announced.
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const [serverResults, setServerResults] = useState<UserSearchResult[]>([]);
  // Truncation stats from the LAST fetch, before excludeIds filtering — "shown"
  // is what the server actually returned, never the post-exclude display count,
  // so the hint never conflates server-side capping with local exclusion.
  // null total = header missing/unparseable: "unknown", never "no more".
  const [serverStats, setServerStats] = useState<{ shown: number; total: number | null } | null>(
    null,
  );
  const [serverStatus, setServerStatus] = useState<ServerStatus>('idle');

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic sequence guard: a slow "AB" must not overwrite the newer "ABC".
  const searchSeq = useRef(0);

  // useId, not a module constant: two comboboxes on one screen would otherwise
  // emit the same DOM id, making each one's aria-controls ambiguous.
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listboxId = `${generatedId}-listbox`;

  // The data source is a fresh object literal on every render, so the debounced
  // callback reads it through a ref instead of closing over a stale copy.
  const dataSourceRef = useRef(dataSource);
  useEffect(() => {
    dataSourceRef.current = dataSource;
  });

  // Keep the portalled dropdown glued to the input. Recompute on any scroll
  // (capture: true so the dialog's own overflow-y-auto scroll counts) and on
  // resize — including the iOS keyboard, which fires visualViewport resize.
  const updateAnchor = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchor({ top: r.bottom, left: r.left, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateAnchor();
    window.addEventListener('scroll', updateAnchor, true);
    window.addEventListener('resize', updateAnchor);
    window.visualViewport?.addEventListener('resize', updateAnchor);
    return () => {
      window.removeEventListener('scroll', updateAnchor, true);
      window.removeEventListener('resize', updateAnchor);
      window.visualViewport?.removeEventListener('resize', updateAnchor);
    };
  }, [isOpen, updateAnchor]);

  // Escape closes the suggestion list only — never the parent dialog. Radix's
  // DismissableLayer listens for Escape on `document` in the CAPTURE phase, so a
  // React (bubble-phase) handler on the input can't preempt it. We register our
  // own capture listener on mount: this combobox is a descendant of the dialog's
  // DismissableLayer, and React runs descendant effects before ancestor ones,
  // so ours is added first and fires first. While the list is open it swallows
  // the Escape (stopImmediatePropagation) and closes only the list; when the
  // list is closed it does nothing, so Escape dismisses the dialog as usual.
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);
  useEffect(() => {
    function onKeyDownCapture(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpenRef.current) {
        e.stopImmediatePropagation();
        setIsOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDownCapture, true);
    return () => document.removeEventListener('keydown', onKeyDownCapture, true);
  }, []);

  // Close on outside click. The dropdown is portalled out of containerRef, so a
  // click on a suggestion would otherwise read as "outside" and close the list
  // on pointerdown before the pick lands. Treat pointerdowns inside EITHER the
  // input container or the portalled dropdown as inside.
  useEffect(() => {
    function handlePointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (containerRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    }
    // pointerdown, not mousedown: iOS Safari does not synthesise mouse events
    // for a tap on a non-interactive element, so a nurse tapping the dialog
    // backdrop to dismiss the list would find it stuck open on the iPad this
    // app is built for. This replaces the setTimeout-on-blur dismissal the two
    // server-backed pickers used, which raced the click that selected an option.
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const runServerSearch = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < MIN_USER_SEARCH_QUERY_LENGTH) {
      searchSeq.current++; // invalidate anything in flight
      setServerResults([]);
      setServerStats(null);
      setServerStatus('idle');
      return;
    }
    const seq = ++searchSeq.current;
    setServerStatus('searching');
    debounceRef.current = setTimeout(() => {
      const source = dataSourceRef.current;
      if (source.type !== 'server') return;
      void source.searchFn(query).then(
        ({ results, totalCount }) => {
          if (seq !== searchSeq.current) return; // superseded by a newer search
          setServerResults(results);
          setServerStats({ shown: results.length, total: totalCount });
          setServerStatus('ready');
        },
        () => {
          if (seq !== searchSeq.current) return;
          // A search FAILURE renders distinctly from "no matches" — the list
          // stays open showing the error instead of silently closing, which
          // used to make a broken search look like an honest empty result.
          setServerResults([]);
          setServerStats(null);
          setServerStatus('failed');
        },
      );
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const isServer = dataSource.type === 'server';
  const clientUsers = dataSource.type === 'client' ? dataSource.users : undefined;
  const clientError = dataSource.type === 'client' && dataSource.error === true;

  const matched = useMemo(() => {
    if (isServer) return serverResults;
    if (!clientUsers) return [];
    if (!value) return clientUsers;
    const q = value.toLowerCase();
    return clientUsers.filter(
      (u) =>
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.role && u.role.toLowerCase().includes(q)) ||
        u.id.toLowerCase().includes(q),
    );
  }, [isServer, serverResults, clientUsers, value]);

  const options =
    excludeIds && excludeIds.length > 0
      ? matched.filter((u) => !excludeIds.includes(u.id))
      : matched;

  // Reset the highlight whenever the list content or open-state changes, so a
  // stale index never carries over to a different result set.
  useEffect(() => {
    setFocusedIdx(-1);
  }, [value, isOpen]);

  // Keep the highlighted option scrolled into view as arrow keys move it.
  useEffect(() => {
    if (focusedIdx < 0) return;
    document.getElementById(`${listboxId}-${focusedIdx}`)?.scrollIntoView({ block: 'nearest' });
  }, [focusedIdx, listboxId]);

  const commitSelect = useCallback(
    (user: UserSearchResult) => {
      setIsOpen(false);
      onSelect(user);
    },
    [onSelect],
  );

  function handleInputChange(next: string) {
    onChange(next);
    setIsOpen(true);
    if (isServer) runServerSearch(next);
  }

  const hasError = isServer ? serverStatus === 'failed' : clientError;
  // "Nothing matches" is only true once we have actually looked: for a client
  // list that is as soon as the user types, for the server it is a completed
  // search.
  const searched = isServer ? serverStatus === 'ready' : value.length > 0;
  const showResults = isOpen && !hasError && options.length > 0;
  const showError = isOpen && hasError;
  const showEmpty = isOpen && !hasError && searched && options.length === 0;
  const showTruncation =
    showResults && serverStats !== null && serverStats.total !== null && serverStats.total > serverStats.shown;

  return (
    <div
      ref={containerRef}
      className="relative"
      // Tabbing away closes the list. Without this the portalled list stays
      // suspended over the dialog, detached from the input that owns it, until
      // an outside click happens to land.
      //
      // Safe here only because an option press calls preventDefault (see below)
      // and never takes focus: focus-out therefore means the user really left,
      // not that they are mid-pick. That distinction is what the deleted
      // `setTimeout(…, 200)` in the two original pickers was guessing at, and
      // guessing wrong often enough to eat clicks.
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        if (dropdownRef.current?.contains(e.relatedTarget)) return;
        setIsOpen(false);
      }}
    >
      <Search
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
      />
      <Input
        id={inputId}
        // A stable, unique name keeps Safari from offering its own autofill
        // menu on top of this list.
        name={inputId}
        type="search"
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        // Only reference the listbox when it actually exists in the DOM — in the
        // empty/error state there is no listbox, so an unconditional
        // aria-controls would be a dangling reference.
        aria-controls={showResults ? listboxId : undefined}
        aria-activedescendant={focusedIdx >= 0 ? `${listboxId}-${focusedIdx}` : undefined}
        placeholder={placeholder ?? t('admin.userSelect.searchPlaceholder', { defaultValue: 'Search users...' })}
        value={value}
        disabled={disabled}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setIsOpen(true)}
        onKeyDown={(e) => {
          // Arrow/Enter operate the listbox the ARIA roles advertise. Escape is
          // handled by the capture listener above (it must beat Radix); these
          // keys Radix ignores, so a normal bubble handler is fine.
          if (!isOpen || options.length === 0) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIdx((i) => (i + 1) % options.length);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIdx((i) => (i <= 0 ? options.length - 1 : i - 1));
          } else if (e.key === 'Enter' && focusedIdx >= 0) {
            e.preventDefault();
            commitSelect(options[focusedIdx]);
          }
        }}
        className="min-h-[44px] pl-9 text-sm"
      />

      {(showResults || showEmpty || showError) &&
        anchor &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[60] mt-1 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg"
            // pointerEvents is load-bearing, not styling. Radix's
            // DismissableLayer sets `document.body.style.pointerEvents='none'`
            // while a modal Dialog/Sheet is open and re-enables `auto` only on
            // its OWN layer. This list is portalled to <body>, a SIBLING of that
            // layer, so it inherits the lock: it renders, sits on top via
            // z-[60], and the mouse passes straight through — the options are
            // unclickable and even :hover is dead, while keyboard Arrow/Enter
            // still works. Inline rather than a utility class so it cannot be
            // lost to purging or lose a specificity fight.
            style={{
              top: anchor.top,
              left: anchor.left,
              width: anchor.width,
              pointerEvents: 'auto',
            }}
            // The dropdown is portalled to <body>, i.e. OUTSIDE the Radix
            // DialogContent. Radix's DismissableLayer listens for pointerdown on
            // document and would treat a click here as an outside-interaction —
            // closing the whole dialog before the suggestion's onClick fires.
            // Stopping propagation keeps the event from reaching that handler.
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {showError ? (
              <div role="alert" className="p-3 text-sm text-error">
                {isServer
                  ? t('sharing.searchFailed', { defaultValue: 'Search failed. Please try again.' })
                  : t('admin.userSelect.loadFailed', { defaultValue: 'Could not load users.' })}
              </div>
            ) : showEmpty ? (
              // role=status, not an empty listbox: ARIA 1.2 requires a listbox
              // to contain only `option`s, so the message is a polite live
              // region instead.
              <div role="status" aria-live="polite" className="p-3 text-sm text-neutral-500">
                {t('admin.userSelect.noResults', { defaultValue: 'No users found' })}
              </div>
            ) : (
              <div id={listboxId} role="listbox" className="max-h-60 overflow-y-auto">
                {options.map((user, idx) => {
                  const label = userDisplayName(user);
                  const detail = userDetail(user);
                  const picked = selectedUserId != null && selectedUserId === user.id;
                  return (
                    <button
                      key={user.id}
                      // Without an explicit type a <button> inside a dialog's
                      // <form> defaults to submit: picking a name submitted the
                      // dialog, so the selection vanished instead of sticking.
                      type="button"
                      // A listbox option must not be its own tab stop —
                      // navigation is Arrow keys + aria-activedescendant. Left
                      // focusable, Tab from the input walks into the portal and
                      // the user has to tab past every match to reach the next
                      // field.
                      tabIndex={-1}
                      id={`${listboxId}-${idx}`}
                      role="option"
                      aria-selected={idx === focusedIdx}
                      // The check glyph is decorative; aria-current is what tells
                      // a screen reader which user is already chosen.
                      aria-current={picked ? true : undefined}
                      onMouseEnter={() => setFocusedIdx(idx)}
                      // preventDefault, NOT commit. Pressing an option must not
                      // move focus off the input: the option unmounts the moment
                      // a pick lands, and the browser would drop the orphaned
                      // focus on <body>. Keeping focus in the input is also what
                      // makes closing on focus-out safe below — focus only ever
                      // leaves when the user genuinely tabs away.
                      //
                      // Committing HERE instead would break touch: on an iPad,
                      // pointerdown fires the instant a finger lands, so a nurse
                      // starting to SCROLL the list would select whoever she
                      // touched. Commit belongs on click, which requires the
                      // press and the release on the same option.
                      onPointerDown={(e) => e.preventDefault()}
                      onClick={() => commitSelect(user)}
                      className={cn(
                        'flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors',
                        'border-b border-neutral-100 last:border-b-0',
                        idx === focusedIdx ? 'bg-neutral-100' : 'hover:bg-neutral-50',
                      )}
                    >
                      <span
                        aria-hidden
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[13px] font-semibold text-primary-700"
                      >
                        {label.trim().charAt(0).toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-neutral-900">{label}</span>
                        {detail && (
                          <span className="block truncate text-xs text-neutral-500">{detail}</span>
                        )}
                      </span>
                      {picked && (
                        <Check className="h-4 w-4 flex-shrink-0 text-primary-500" aria-hidden />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Non-interactive truncation hint — never a pagination control,
                just a nudge to narrow the search. Outside the listbox: a
                listbox may own nothing but options. */}
            {showTruncation && (
              <div className="border-t border-neutral-100 bg-neutral-50 px-3 py-2 text-xs text-neutral-500">
                {t('sharing.searchTruncated', {
                  shown: serverStats.shown,
                  total: serverStats.total,
                  defaultValue: 'Showing {{shown}} of {{total}} — refine your search',
                })}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
